<script setup lang="ts" generic="T extends string | number = string">
import FinestCountBadge from '@/components/FinestCountBadge.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export interface SectionTab<TValue extends string | number = string> {
  label: string
  value: TValue
  count?: number
  complete?: boolean
  icon?: string
  trailing?: boolean
  disabled?: boolean
}

const props = defineProps<{
  tabs: SectionTab<T>[]
  ariaLabel?: string
}>()

const model = defineModel<T>({ required: true })

const emit = defineEmits<{
  change: [value: T]
}>()

const { t } = useI18n({ useScope: 'local' })

const tablistLabel = computed(() => props.ariaLabel ?? t('ariaLabel'))

function select(tab: SectionTab<T>): void {
  if (tab.disabled || tab.value === model.value) {
    return
  }

  model.value = tab.value
  emit('change', tab.value)
}

function tabId(value: T): string {
  return `section-tab-${String(value)}`
}
</script>

<template>
  <div class="border-line mb-6 flex flex-wrap gap-0.5 border-b" role="tablist" :aria-label="tablistLabel">
    <template v-for="tab in tabs" :key="String(tab.value)">
      <span v-if="tab.trailing" class="bg-line my-2 ml-auto w-px flex-none" aria-hidden="true" />

      <button
        :id="tabId(tab.value)"
        type="button"
        role="tab"
        class="text-ink-3 enabled:hover:text-ink focus-visible:ring-action/35 disabled:text-line -mb-px inline-flex min-w-0 cursor-pointer items-center gap-2 border-b-2 border-transparent px-4 py-2.5 text-base font-medium transition-colors focus-visible:rounded-t-md focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed"
        :class="{ 'border-action! text-action!': tab.value === model }"
        :aria-selected="tab.value === model"
        :tabindex="tab.disabled ? -1 : 0"
        :disabled="tab.disabled"
        @click="select(tab)">
        <i v-if="tab.icon" :class="tab.icon" class="text-sm!" aria-hidden="true" />
        <span class="min-w-0 break-words">{{ tab.label }}</span>
        <FinestCountBadge :count="tab.count ?? 0" :complete="tab.complete" />
      </button>
    </template>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "ariaLabel": "Abschnitte"
  },
  "en": {
    "ariaLabel": "Sections"
  }
}
</i18n>
